import { AlertTriangleIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { useLogout } from '../../hooks/mutations/useLogout';
import { useCurrentSession } from '../../hooks/queries/useCurrentSession';
import logger from '../../lib/logger';
import { PATHS } from '../../paths';

interface IBaseImpersonatorNoticeProps {
  /**
   * Callback when the logout button is clicked.
   */
  onLogout: () => void;
}

/**
 * Base visual component for displaying an impersonator notice.
 * Use this component directly when you need to control the logout behavior manually.
 */
export const BaseImpersonatorNotice = ({ onLogout }: IBaseImpersonatorNoticeProps) => {
  return (
    <div className="bg-utility-error-50 ring-utility-error-200 m-0 flex items-center justify-start gap-2 px-6 py-5 ring-1 ring-inset md:mx-4 md:mt-3 md:rounded-xl lg:mx-8">
      <AlertTriangleIcon className="text-utility-error-700 size-6 shrink-0" />
      <span className="text-utility-error-700 text-base font-medium">You are currently impersonating another user</span>
      <button
        onClick={onLogout}
        className="text-utility-error-500 hover:text-utility-error-700 text-base font-medium underline hover:no-underline"
        type="button"
      >
        Logout
      </button>
    </div>
  );
};

/**
 * Connected component that uses session hooks to determine impersonation state.
 * Only renders when the current session is impersonating another user.
 */
const ImpersonatorNotice = () => {
  const { session } = useCurrentSession();
  const [logout] = useLogout();
  const impersonatorUserId = session?.impersonatorUserId;

  const handleLogout = React.useCallback(async () => {
    try {
      await logout({ variables: {} });
      window.location.href = PATHS.LOGIN;
    } catch (error) {
      logger.error('Failed to logout:', error);
    }
  }, [logout]);

  if (!impersonatorUserId) {
    return null;
  }

  return <BaseImpersonatorNotice onLogout={handleLogout} />;
};

export default ImpersonatorNotice;
